import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { startToolOperation, trackToolRejection, type ToolAnalyticsContext } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT } from '@/shared/i18n'
import { exportScannedPdf } from '@/features/document-scanner/model/export'
import { OutputPixelBudgetError } from '@/features/document-scanner/model/processor'
import {
  DEFAULT_TRANSFORM,
  MAX_IMAGE_PIXELS,
  MAX_OUTPUT_PIXELS,
  MAX_PAGES,
  MAX_TOTAL_BYTES,
  ScannerValidationError,
  movePage,
  sourceCropForRotation,
  validateScannerFiles,
  type PageTransform,
  type ScannerPage,
} from '@/features/document-scanner/model/lib'

const ANALYTICS = {
  app: 'pdfy',
  domain: 'pdfy.ru',
  route: '/skaner-dokumentov-online',
  utility: 'document-scanner',
  processingMode: 'local',
} satisfies ToolAnalyticsContext

interface DocumentScannerFormProps {
  exportOverride?: (pages: ScannerPage[], signal?: AbortSignal) => Promise<Blob>
}

function updateTransform(page: ScannerPage, patch: Partial<PageTransform>): ScannerPage {
  return { ...page, transform: { ...page.transform, ...patch } }
}

async function createThumbnails(files: Array<{ file: File; width: number; height: number }>, signal: AbortSignal): Promise<string[]> {
  if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined') throw new Error('thumbnail-unsupported')
  const worker = new Worker(new URL('../model/thumbnail.worker.ts', import.meta.url), { type: 'module' })
  const urls: string[] = []
  const abort = () => worker.terminate()
  signal.addEventListener('abort', abort, { once: true })
  try {
    for (const item of files) {
      if (signal.aborted) throw new DOMException('Cancelled', 'AbortError')
      const bytes = await new Promise<ArrayBuffer>((resolve, reject) => {
        const onAbort = () => reject(new DOMException('Cancelled', 'AbortError'))
        signal.addEventListener('abort', onAbort, { once: true })
        worker.onmessage = (event: MessageEvent<{ bytes?: ArrayBuffer; error?: string }>) => {
          signal.removeEventListener('abort', onAbort)
          if (event.data.error || !event.data.bytes) reject(new Error(event.data.error ?? 'Thumbnail failed'))
          else resolve(event.data.bytes)
        }
        worker.onerror = () => { signal.removeEventListener('abort', onAbort); reject(new Error('Thumbnail worker failed')) }
        worker.postMessage({ file: item.file, width: item.width, height: item.height })
      })
      if (signal.aborted) throw new DOMException('Cancelled', 'AbortError')
      urls.push(URL.createObjectURL(new Blob([bytes], { type: 'image/jpeg' })))
    }
    return urls
  } catch (error) {
    urls.forEach((url) => URL.revokeObjectURL(url))
    throw error
  } finally {
    signal.removeEventListener('abort', abort)
    worker.terminate()
  }
}

function previewStyle(page: ScannerPage): CSSProperties {
  const crop = sourceCropForRotation(page.transform.crop, page.transform.rotation)
  return {
    transform: `rotate(${page.transform.rotation}deg)`,
    clipPath: `inset(${crop.top}% ${crop.right}% ${crop.bottom}% ${crop.left}%)`,
    filter: `${page.transform.mode === 'color' ? '' : 'grayscale(1)'} brightness(${100 + page.transform.brightness}%) contrast(${100 + page.transform.contrast + (page.transform.mode === 'black-white' ? 80 : 0)}%)`,
  }
}

export function DocumentScannerForm({ exportOverride = exportScannedPdf }: DocumentScannerFormProps) {
  const t = useT()
  const copy = t.documentScanner
  const formatBytes = useFormatBytes()
  const galleryRef = useRef<HTMLInputElement>(null)
  const cameraRef = useRef<HTMLInputElement>(null)
  const pagesRef = useRef<ScannerPage[]>([])
  const addQueueRef = useRef<Promise<void>>(Promise.resolve())
  const busyRef = useRef(false)
  const abortRef = useRef<AbortController | null>(null)
  const thumbnailAbortRef = useRef<AbortController | null>(null)
  const resultUrlRef = useRef<string | null>(null)
  const mountedRef = useRef(true)
  const [pages, setPages] = useState<ScannerPage[]>([])
  const [busy, setBusy] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ url: string; size: number } | null>(null)

  useEffect(() => { pagesRef.current = pages }, [pages])
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      abortRef.current?.abort()
      thumbnailAbortRef.current?.abort()
      pagesRef.current.forEach((page) => URL.revokeObjectURL(page.previewUrl))
      if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current)
    }
  }, [])

  function clearResult() {
    if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current)
    resultUrlRef.current = null
    setResult(null)
  }

  function setBusyState(value: boolean) {
    busyRef.current = value
    setBusy(value)
  }

  function addFiles(input: FileList | File[]) {
    const files = Array.from(input)
    if (files.length === 0 || busyRef.current) return
    clearResult()
    setError(null)
    addQueueRef.current = addQueueRef.current.then(() => processFiles(files))
  }

  async function processFiles(files: File[]) {
    const thumbnailController = new AbortController()
    thumbnailAbortRef.current = thumbnailController
    try {
      const validated = await validateScannerFiles(files, pagesRef.current)
      if (!mountedRef.current) return
      const previewUrls = await createThumbnails(validated, thumbnailController.signal)
      if (!mountedRef.current) {
        previewUrls.forEach((url) => URL.revokeObjectURL(url))
        return
      }
      const added = validated.map(({ file, width, height }, index) => ({
        id: `${file.name}-${file.size}-${file.lastModified}-${crypto.randomUUID()}`,
        file,
        width,
        height,
        previewUrl: previewUrls[index]!,
        transform: { ...DEFAULT_TRANSFORM, crop: { ...DEFAULT_TRANSFORM.crop } },
      }))
      const next = [...pagesRef.current, ...added]
      pagesRef.current = next
      setPages(next)
    } catch (cause) {
      if (!mountedRef.current || (cause instanceof DOMException && cause.name === 'AbortError')) return
      const validation = cause instanceof ScannerValidationError ? cause : null
      trackToolRejection(ANALYTICS, validation?.code ?? 'invalid_input')
      setError(validation ? copy.errors[validation.reason] : copy.errors.corrupt)
    } finally {
      if (thumbnailAbortRef.current === thumbnailController) thumbnailAbortRef.current = null
    }
  }

  function changePage(id: string, updater: (page: ScannerPage) => ScannerPage) {
    if (busyRef.current) return
    clearResult()
    setPages((current) => current.map((page) => page.id === id ? updater(page) : page))
  }

  function removePage(id: string) {
    if (busyRef.current) return
    clearResult()
    setPages((current) => {
      const removed = current.find((page) => page.id === id)
      if (removed) URL.revokeObjectURL(removed.previewUrl)
      return current.filter((page) => page.id !== id)
    })
  }

  function reorder(index: number, direction: -1 | 1) {
    if (busyRef.current) return
    clearResult()
    setPages((current) => movePage(current, index, direction))
  }

  function setCrop(page: ScannerPage, side: keyof PageTransform['crop'], value: number) {
    const next = Math.max(0, Math.min(40, value))
    const opposite = side === 'left' ? 'right' : side === 'right' ? 'left' : side === 'top' ? 'bottom' : 'top'
    if (next + page.transform.crop[opposite] > 80) return
    changePage(page.id, (current) => updateTransform(current, { crop: { ...current.transform.crop, [side]: next } }))
  }

  async function exportPdf() {
    if (pages.length === 0 || busyRef.current) return
    clearResult()
    setError(null)
    setBusyState(true)
    const controller = new AbortController()
    abortRef.current = controller
    const operation = startToolOperation(ANALYTICS)
    const startedAt = performance.now()
    try {
      await addQueueRef.current
      if (controller.signal.aborted || !mountedRef.current || abortRef.current !== controller) return
      const committedPages = pagesRef.current
      if (committedPages.length === 0) return
      const blob = await exportOverride(committedPages, controller.signal)
      if (controller.signal.aborted || !mountedRef.current || abortRef.current !== controller) return
      if (blob.type !== 'application/pdf') throw new Error('Unexpected output')
      const url = URL.createObjectURL(blob)
      resultUrlRef.current = url
      operation.success(performance.now() - startedAt)
      setResult({ url, size: blob.size })
    } catch (cause) {
      if (controller.signal.aborted || !mountedRef.current || abortRef.current !== controller) return
      operation.error(cause instanceof DOMException && cause.name === 'AbortError' ? 'cancelled' : 'processing_failed')
      setError(cause instanceof OutputPixelBudgetError || (cause instanceof Error && cause.message === 'Output pixel budget exceeded')
        ? copy.errors.output_limit
        : copy.errors.processing)
    } finally {
      if (mountedRef.current && abortRef.current === controller) {
        abortRef.current = null
        setBusyState(false)
      }
    }
  }

  const totalBytes = pages.reduce((sum, page) => sum + page.file.size, 0)

  return (
    <Card className="mb-10">
      <CardContent className="space-y-6 pt-6">
        <div
          role="button"
          tabIndex={busy ? -1 : 0}
          aria-label={copy.pickGallery}
          className={cn('rounded-xl border-2 border-dashed p-6 sm:p-8 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors', dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50', busy && 'pointer-events-none opacity-50')}
          onClick={() => !busy && galleryRef.current?.click()}
          onKeyDown={(event) => { if (!busy && (event.key === 'Enter' || event.key === ' ')) galleryRef.current?.click() }}
          onDragOver={(event) => { event.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => { event.preventDefault(); setDragging(false); addFiles(event.dataTransfer.files) }}
        >
          <input ref={galleryRef} className="hidden" type="file" accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" multiple onChange={(event) => { addFiles(event.currentTarget.files ?? []); event.currentTarget.value = '' }} />
          <p className="font-medium text-foreground">{copy.drop}</p>
          <p className="mt-2 text-xs text-muted-foreground">{format(copy.limits, { pages: MAX_PAGES, size: MAX_TOTAL_BYTES / 1024 / 1024, megapixels: MAX_IMAGE_PIXELS / 1_000_000, outputMegapixels: MAX_OUTPUT_PIXELS / 1_000_000 })}</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input ref={cameraRef} className="hidden" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={(event) => { addFiles(event.currentTarget.files ?? []); event.currentTarget.value = '' }} />
          <Button type="button" variant="outline" disabled={busy || pages.length >= MAX_PAGES} onClick={() => cameraRef.current?.click()}>{copy.camera}</Button>
          <span className="text-xs text-muted-foreground">{copy.localNotice}</span>
        </div>

        {pages.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm"><strong>{format(copy.pageCount, { count: pages.length })}</strong><span>{formatBytes(totalBytes)}</span></div>
            {pages.map((page, index) => (
              <article key={page.id} className="rounded-xl border bg-background p-4">
                <div className="grid gap-4 md:grid-cols-[180px_1fr]">
                  <div className="relative flex min-h-44 items-center justify-center overflow-hidden rounded-lg bg-muted p-2">
                    <img
                      src={page.previewUrl}
                      alt={format(copy.previewAlt, { page: index + 1 })}
                      loading="lazy"
                      decoding="async"
                      className="max-h-48 max-w-full object-contain"
                      style={previewStyle(page)}
                    />
                  </div>
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <strong className="mr-auto text-sm">{format(copy.pageTitle, { page: index + 1 })}</strong>
                      <Button type="button" variant="outline" size="sm" disabled={busy || index === 0} aria-label={format(copy.moveUp, { page: index + 1 })} onClick={() => reorder(index, -1)}>↑</Button>
                      <Button type="button" variant="outline" size="sm" disabled={busy || index === pages.length - 1} aria-label={format(copy.moveDown, { page: index + 1 })} onClick={() => reorder(index, 1)}>↓</Button>
                      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => removePage(page.id)}>{copy.remove}</Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => changePage(page.id, (current) => updateTransform(current, { rotation: ((current.transform.rotation + 90) % 360) as PageTransform['rotation'] }))}>{copy.rotate}</Button>
                      {(['color', 'grayscale', 'black-white'] as const).map((mode) => <Button key={mode} type="button" size="sm" variant={page.transform.mode === mode ? 'default' : 'outline'} disabled={busy} onClick={() => changePage(page.id, (current) => updateTransform(current, { mode }))}>{copy.modes[mode]}</Button>)}
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="text-xs">{copy.brightness}: {page.transform.brightness}<input className="mt-1 w-full" type="range" min="-40" max="40" value={page.transform.brightness} disabled={busy} onChange={(event) => changePage(page.id, (current) => updateTransform(current, { brightness: Number(event.target.value) }))} /></label>
                      <label className="text-xs">{copy.contrast}: {page.transform.contrast}<input className="mt-1 w-full" type="range" min="-40" max="60" value={page.transform.contrast} disabled={busy} onChange={(event) => changePage(page.id, (current) => updateTransform(current, { contrast: Number(event.target.value) }))} /></label>
                    </div>
                    <fieldset className="grid grid-cols-2 gap-2 sm:grid-cols-4"><legend className="mb-1 text-xs font-medium">{copy.crop}</legend>{(['top', 'right', 'bottom', 'left'] as const).map((side) => <label key={side} className="text-xs">{copy.cropSides[side]}<input aria-label={`${copy.cropSides[side]} ${index + 1}`} className="mt-1 w-full rounded border bg-background px-2 py-1" type="number" min="0" max="40" value={page.transform.crop[side]} disabled={busy} onChange={(event) => setCrop(page, side, Number(event.target.value))} /></label>)}</fieldset>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <p className="rounded-lg bg-muted/60 p-3 text-xs text-muted-foreground">{copy.disclaimer}</p>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" disabled={pages.length === 0 || busy} onClick={() => void exportPdf()}>{busy ? copy.processing : copy.export}</Button>
          {result && <a className="inline-flex rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground" href={result.url} download="scan.pdf">{copy.download} ({formatBytes(result.size)})</a>}
        </div>
      </CardContent>
    </Card>
  )
}
